import {
  isValidAmount,
  constructAmountRequirementsError,
  toXdrAmount,
  setSourceAccount,
} from "../util/operations.js";
import {
  LiquidityPoolWithdrawOp,
  Operation,
  OperationBody,
  PoolId,
} from "../../xdr/index.js";
import { hexToUint8Array } from "uint8array-extras";
import { LiquidityPoolWithdrawOpts, OperationAttributes } from "./types.js";

/**
 * Creates a liquidity pool withdraw operation.
 *
 * @see https://developers.stellar.org/docs/start/list-of-operations/#liquidity-pool-withdraw
 *
 * @param opts - Options object
 *   - `liquidityPoolId`: The liquidity pool ID.
 *   - `amount`: Amount of pool shares to withdraw.
 *   - `minAmountA`: Minimum amount of first asset to withdraw.
 *   - `minAmountB`: Minimum amount of second asset to withdraw.
 *   - `source`: The source account for the operation. Defaults to the transaction's source account.
 */
export function liquidityPoolWithdraw(
  opts: LiquidityPoolWithdrawOpts = {} as LiquidityPoolWithdrawOpts,
): Operation {
  if (!opts.liquidityPoolId) {
    throw new TypeError("liquidityPoolId argument is required");
  }
  const liquidityPoolId = new PoolId(hexToUint8Array(opts.liquidityPoolId));

  if (!isValidAmount(opts.amount)) {
    throw new TypeError(constructAmountRequirementsError("amount"));
  }
  const amount = toXdrAmount(opts.amount);

  if (!isValidAmount(opts.minAmountA, true)) {
    throw new TypeError(constructAmountRequirementsError("minAmountA"));
  }
  const minAmountA = toXdrAmount(opts.minAmountA);

  if (!isValidAmount(opts.minAmountB, true)) {
    throw new TypeError(constructAmountRequirementsError("minAmountB"));
  }
  const minAmountB = toXdrAmount(opts.minAmountB);

  const liquidityPoolWithdrawOp = new LiquidityPoolWithdrawOp({
    liquidityPoolId: liquidityPoolId,
    amount,
    minAmountA,
    minAmountB,
  });

  const opAttributes: OperationAttributes = {
    sourceAccount: null,
    body: OperationBody.liquidityPoolWithdraw(liquidityPoolWithdrawOp),
  };
  setSourceAccount(opAttributes, opts);

  return new Operation(opAttributes);
}
